import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'; // use WebHistory in the browser
import { DefaultLayout } from '../lib/layouts/layouts';
import StartView from '../lib/pages/StartView.vue';
import MedicalWarningView from '../lib/pages/MedicalWarningView.vue';
import Error from '../lib/pages/Error.vue';

import medicalRequestChildren from './medical-request';
import adminRequestChildren from './admin-request';
import { defaultMeta, defaultNhsErrorMeta } from './routesSetup';
import repeatPrescriptionRequestChildren from './repeat-prescription-request';

import { patchTenantAwarePush, registerTenantGuards } from './tenantRouting';
import HomeView from '../lib/pages/HomeView.vue';
import NhsLoginView from '../lib/pages/NhsLoginView.vue';
import SessionView from '../lib/pages/SessionView.vue';
import ErrorLayout from '../lib/layouts/ErrorLayout.vue';
import EmptyView from '../lib/pages/EmptyView.vue';
import NhsAppSsoView from '../lib/pages/NhsAppSsoView.vue';
import PageView from '../lib/pages/PageView.vue';
import { youHaveLoggedOut } from '@/data/pages/you-have-logged-out';

const routes: RouteRecordRaw[] = [
  {
    path: '',
    component: HomeView,
    name: 'start-no-tenant',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'This page isn’t available',
      pageTitle: 'This page isn’t available',
      backButton: false,
    },
  },
  {
    path: '/nhs-login-callback',
    component: NhsLoginView,
    name: 'start-no-tenant-nhs-login',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Getting your data...',
      pageTitle: 'Getting your data...',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
      },
    },
  },
  {
    path: '/:section/nhs-app-callback',
    component: NhsAppSsoView,
    name: 'start-no-tenant-nhs-app-callback',
    meta: {
      requiresLoadedTenant: false,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Getting your data...',
      pageTitle: 'Getting your data...',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
      },
    },
  },
  {
    path: '/session/expired',
    name: 'session-expired',
    component: SessionView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Your session has ended',
      pageTitle: 'Your session has ended',
    },
  },
  {
    path: '/:tenantId(\\w{6})/session/expired',
    name: 'session-expired-tenant',
    component: SessionView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Your session has ended',
      pageTitle: 'Your session has ended',
    },
  },
  {
    path: '/:tenantId(\\w{6})/start',
    component: StartView,
    name: 'start-home',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'How can we help?',
      pageTitle: 'How can we help?',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  // NB. general 'pages' can be loaded here with content files in data/pages/
  {
    path: '/:tenantId(\\w{6})/page/you-have-logged-out',
    name: 'page-with-tenant-logout',
    component: PageView,
    meta: {
      pageTitle: youHaveLoggedOut.title,
      pageContent: youHaveLoggedOut.contentBlocks,
      backButton: youHaveLoggedOut.backButton,
      nhsAppActions: {
        showBackNhsApp: false,
      },
    },
  },
  // NB. routes for pages with content files in data/pages/
  {
    path: '/:tenantId(\\w{6})/page/:pathMatch(.*)*',
    name: 'page-with-tenant',
    component: PageView,
    meta: {
      pageTitle: 'Page',
      nhsAppActions: {
        showBackNhsApp: true,
        setBackAction: 'PP_GO_BACK',
        goToPage: 'PP_GO_BACK',
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/page/:pathMatch(.*)*',
    name: 'page-no-tenant',
    component: PageView,
    meta: {
      pageTitle: 'Page',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: true,
        setBackAction: 'PP_GO_BACK',
        goToPage: 'PP_GO_BACK',
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/:tenantId(\\w{6})/error',
    component: Error,
    name: 'tenant-error',
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: 'Error',
      pageTitle: 'Error',
      backButton: false,
      ...defaultNhsErrorMeta,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  {
    path: '/:tenantId(\\w{6})/error/400',
    name: 'error-400-tenant-present',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: '404',
      pageTitle: '404',
      backButton: false,
      ...defaultNhsErrorMeta,
    },
  },
  {
    path: '/error/400',
    name: 'error-400',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: '404',
      pageTitle: '404',
      backButton: false,
      ...defaultNhsErrorMeta,
    },
  },
  {
    path: '/error/400',
    name: 'error-400',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: '404',
      pageTitle: '404',
      backButton: false,
      ...defaultNhsErrorMeta,
      nhsAppActions: {
        showBackNhsApp: false,
      },
    },
  },
  {
    path: '/error',
    name: 'error-main',
    component: Error,
    meta: {
      ...defaultMeta,
      layoutComponent: ErrorLayout,
      layoutPageTitle: 'Error',
      pageTitle: 'Error',
      backButton: false,
      ...defaultNhsErrorMeta,
      nhsAppActions: {
        showBackNhsApp: false,
      },
    },
  },
  {
    path: '/:tenantId(\\w{6})',
    component: StartView,
    name: 'start',
    meta: {
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'How can we help?',
      pageTitle: 'How can we help?',
      backButton: false,
      nhsAppActions: {
        showBackNhsApp: false,
        showBackNhsAppWeb: false,
      },
    },
  },
  // Medical action routes
  {
    path: '/:tenantId(\\w{6})/medical-request',
    name: 'medical-request-wr',
    component: MedicalWarningView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: "Check it's not an emergency",
      pageTitle: "Check it's not an emergency",
      requiresLoadedTenant: true,
      nhsAppActions: {
        setBackAction: 'GO_BACK',
        goToPage: 'GO_BACK',
        showBackNhsApp: true,
        showBackNhsAppWeb: false,
      },
    },
    children: medicalRequestChildren,
  },
  // Admin request routes
  {
    path: '/:tenantId(\\w{6})/admin-request',
    name: 'admin-request-wt',
    component: MedicalWarningView,
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Check it’s not an emergency',
      pageTitle: 'Check it’s not an emergency',
      requiresLoadedTenant: true,
      nhsAppActions: {
        setBackAction: 'GO_BACK',
        goToPage: 'GO_BACK',
        showBackNhsApp: true,
        showBackNhsAppWeb: false,
      },
    },
    children: adminRequestChildren,
  },
  // Repeat prescription routes
  {
    path: '/:tenantId(\\w{6})/repeat-prescription-request',
    name: 'repeat-prescription-request-wt',
    component: EmptyView,
    // NB. ENG-20175. No content on this page.
    redirect: {
      name: 'start-home',
    },
    meta: {
      ...defaultMeta,
      layoutComponent: DefaultLayout,
      layoutPageTitle: 'Repeat Prescription Request',
      pageTitle: 'Repeat Prescription Request',
    },
    children: repeatPrescriptionRequestChildren,
  },
  {
    path: '/:tenantId(\\w{6})/:pathMatch(.*)*',
    name: 'tenant-bad-request',
    redirect: (to) => `/${to.params.tenantId}/error/400`,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'bad-request',
    redirect: '/error/400',
  },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0, left: 0 };
  },
});

// Route guards & path prefixing with tenantId
patchTenantAwarePush(router);
registerTenantGuards(router);
